import { ResizableDiv } from '@/components/ui';
import { RESIZE_CONSTRAINTS } from '@/constants';
import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import React from 'react';
import { EditorPanel } from './editor-panel';
import { FileExplorer } from './file-explorer';
import { FileSearch } from './file-search';
import { MenuBar } from './menu-bar';
import { OutputPanel } from './output-panel';
import { StatusBar } from './status-bar';
import { URLRouter } from './url-router';

export const IDEExperience: React.FC = () => {
    const isFileExplorerVisible = useIDESelectors.useFileExplorerVisibility();
    const isOutputPanelVisible = useIDESelectors.useOutputVisibility();
    const { closeSearch } = useIDEState();
    const isSearchOpen = useIDESelectors.useSearchVisibility();

    return (
        <div id='ide-experience' className='flex flex-col h-full bg-vscode-bg text-vscode-text'>
            {/* URL Router - handles URL-based navigation */}
            <URLRouter />

            {/* ROW 1: Menu Bar */}
            <MenuBar />

            {/* ROW 2: Main Content Area */}
            <div id='ide-main-content' className='flex flex-1 overflow-hidden'>
                {/* COLUMN 1: Resizable File Explorer */}
                <ResizableDiv
                    id='ide-file-explorer-sidebar'
                    initialWidth='256px'
                    initialHeight='100%'
                    minWidth={`${RESIZE_CONSTRAINTS.FILE_EXPLORER.MIN_WIDTH_PERCENT * 100}%`}
                    maxWidth={`${RESIZE_CONSTRAINTS.FILE_EXPLORER.MAX_WIDTH_PERCENT * 100}%`}
                    minHeight='100%'
                    maxHeight='100%'
                    resizable={{
                        bottom: false,
                        left: false,
                        right: true,
                        top: false,
                    }}
                    className={`bg-vscode-bg-secondary border-r border-gray-600/30 shadow-sm transition-all duration-200${isFileExplorerVisible ? '' : ' hidden'}`}
                    onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()}
                >
                    <FileExplorer />
                </ResizableDiv>

                {/* COLUMN 2: Editor and Panel */}
                <div id='ide-editor-and-panel' className='flex-1 flex flex-col'>
                    {/* ROW 1: Editor Panel */}
                    <EditorPanel id='ide-editor-panel' />

                    {/* ROW 2: Resizable Output Panel */}
                    <ResizableDiv
                        id='ide-output-panel-container'
                        initialWidth='100%'
                        initialHeight='160px'
                        minWidth='100%'
                        maxWidth='100%'
                        minHeight={`${RESIZE_CONSTRAINTS.OUTPUT_PANEL.MIN_HEIGHT_PERCENT * 100}%`}
                        maxHeight={`${RESIZE_CONSTRAINTS.OUTPUT_PANEL.MAX_HEIGHT_PERCENT * 100}%`}
                        resizable={{
                            bottom: false,
                            left: false,
                            right: false,
                            top: true,
                        }}
                        className={`bg-vscode-bg-secondary border-t border-gray-600/30 shadow-sm${isOutputPanelVisible ? '' : ' hidden'}`}
                    >
                        <OutputPanel id='ide-output-panel' />
                    </ResizableDiv>
                </div>
            </div>

            {/* ROW 3: Status Bar */}
            <StatusBar />

            {/* File Search Modal */}
            <FileSearch isOpen={isSearchOpen} onClose={closeSearch} />
        </div>
    );
};
